import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Reads `?new=<id>` from the URL, scrolls the matching element into view,
 * pulses a highlight ring on it, and then clears the query param after
 * a few seconds so the highlight isn't permanent across reloads.
 *
 * Usage:
 *   const { newId, highlightRef, classForId } = useNewRecordHighlight(loading);
 *   <div ref={id === newId ? highlightRef : null} className={classForId(id)} />
 */
export default function useNewRecordHighlight(loading = false) {
  const [searchParams, setSearchParams] = useSearchParams();
  const newId = searchParams.get("new");
  const highlightRef = useRef(null);

  useEffect(() => {
    if (!newId || loading) return;
    const scrollT = setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const clearT = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }, 6000);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newId, loading]);

  const classForId = (id) => (id === newId ? "dc-highlight-new" : "");

  return { newId, highlightRef, classForId };
}
